import {
  OBSERVATION_CATEGORY_SYSTEM as OBS_CATEGORY_SYSTEM,
  QUESTIONNAIRE_SCORE_SYSTEM,
  UCUM_SYSTEM,
} from "@repo/fhir-systems";
import type { CodeableConcept } from "./common";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { Brand } from "./lib/brand";
import type { DomainId } from "./lib/domain-id";
import type { MemberId } from "./member-identity";
import type { OrderId, OrderSetId } from "./order-set.model";
import type { PatientId } from "./patient.model";
import type { PerformanceMeasureId } from "./performance-measure.model";
import type { PractitionerId } from "./practitioner.model";
import type { ProcedureId } from "./procedure.model";
import type { QuestionnaireInstanceId } from "./questionnaire-instance.model";
import type { UnitValue } from "./value.model";
import type { VisitId } from "./visit.model";

/** Stable Observation identity, independent of the FHIR row locator. */
export type ObservationId = DomainId<"ObservationId">;

export function asObservationId(value: string): ObservationId {
  if (!value) throw new PreconditionFailedError("An observation identity cannot be empty");
  return value as ObservationId;
}

// ============================================================
// Observation — one measurement or finding (ADR-0017)
//
// The lifecycle is about EVIDENTIAL WEIGHT, not workflow: a value is draft
// (`registered`/`preliminary`) until someone stands behind it (`final`), after
// which it can only be corrected on the record (`amended`) — never edited
// silently. That distinction is the whole reason `correct` and `revise` are
// separate operations below rather than one `update`.
//
// Symptoms are minted `final`, so correcting one IS an amendment. The old model
// had this as a second command (`validateSymptomUpdate`) that quietly advanced
// the status; here there is one `correct`, and it amends or not depending on
// whether the observation was ever finalised.
// ============================================================

export const OBSERVATION_STATUSES = [
  "registered",
  "preliminary",
  "final",
  "amended",
  "cancelled",
  "entered-in-error",
] as const;
type ObservationStatus = (typeof OBSERVATION_STATUSES)[number];

interface Quantity {
  value?: number;
  unit?: string;
  system?: string;
  code?: string;
}

interface Component {
  code: CodeableConcept;
  valueQuantity?: Quantity;
  valueCodeableConcept?: CodeableConcept;
  valueString?: string;
  valueBoolean?: boolean;
  valueInteger?: number;
  interpretation?: CodeableConcept[];
}

/** The value, whichever way it was captured. */
interface Value {
  valueQuantity?: Quantity;
  valueCodeableConcept?: CodeableConcept;
  valueString?: string;
  valueBoolean?: boolean;
  valueInteger?: number;
}

const TRANSITIONS: Record<ObservationStatus, ObservationStatus[]> = {
  registered: ["preliminary", "final", "cancelled", "entered-in-error"],
  preliminary: ["final", "cancelled", "entered-in-error"],
  final: ["amended", "entered-in-error"],
  amended: ["amended", "entered-in-error"],
  cancelled: [],
  "entered-in-error": [],
};

/** Nothing may be written to a record in these states — they are closed, not just finished. */
const TERMINAL: ReadonlySet<ObservationStatus> = new Set(["cancelled", "entered-in-error"]);

/** Someone has stood behind this value, so any change is an amendment. */
const STANDS: ReadonlySet<ObservationStatus> = new Set(["final", "amended"]);

export interface ObservationProps extends Value {
  id: ObservationId;
  status: ObservationStatus;
  category: CodeableConcept[];
  code: CodeableConcept;
  patientId: PatientId;
  encounterId?: VisitId;
  effectiveDateTime?: string;
  performerIds?: ObservationPerformerRef[];
  component?: Component[];
  /** Observations this one groups (a panel and its members). */
  memberIds?: ObservationId[];
  /** Stable OrderSet members this observation answers. */
  basedOnOrders?: Array<{ orderSetId: OrderSetId; orderId: OrderId }>;
  /** The clinical event that produced it — typically a Procedure. */
  partOfIds?: ProcedureId[];
  /** What it was extracted from — typically a completed assessment. */
  derivedFromIds?: QuestionnaireInstanceId[];
  performanceMeasureId?: PerformanceMeasureId;
  dataAbsentReason?: CodeableConcept;
  note?: string;
  bodySite?: CodeableConcept;
  interpretation?: CodeableConcept[];
}

export class Observation {
  private state: ObservationProps;

  private constructor(props: ObservationProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Observation requires a patient");
    }
    if (!props.code.coding?.length && !props.code.text) {
      throw new PreconditionFailedError("Observation requires a code");
    }
    this.state = structuredClone(props);
  }

  static create(input: ObservationProps): Observation {
    return new Observation({ ...structuredClone(input), id: asObservationId(input.id) });
  }

  static restore(snapshot: ObservationProps): Observation {
    return new Observation(snapshot);
  }

  static questionnaireScore(input: {
    id: ObservationId;
    instanceId: QuestionnaireInstanceId;
    patientId: PatientId;
    scoreId: MemberId;
    label: string;
    value: number;
    band?: string;
    at: string;
  }): Observation {
    return new Observation({
      id: asObservationId(input.id),
      status: "final",
      patientId: input.patientId,
      category: [{ coding: [{ system: OBS_CATEGORY_SYSTEM, code: "survey" }] }],
      code: { coding: [{ system: QUESTIONNAIRE_SCORE_SYSTEM, code: input.scoreId, display: input.label }] },
      valueInteger: input.value,
      ...(input.band ? { interpretation: [{ text: input.band }] } : {}),
      effectiveDateTime: input.at,
      derivedFromIds: [input.instanceId],
    });
  }

  static performanceMeasureScore(input: {
    id: ObservationId;
    measureId: PerformanceMeasureId;
    patientId: PatientId;
    visit: VisitId;
    clinician: PractitionerId;
    scoreId: MemberId;
    label: string;
    value?: number;
    unit?: UnitValue;
    band?: string;
    at: string;
    dataAbsentReason?: CodeableConcept;
  }): Observation {
    return new Observation({
      id: input.id,
      status: "final",
      patientId: input.patientId,
      encounterId: input.visit,
      performerIds: [{ type: "Practitioner", id: input.clinician }],
      category: [{ coding: [{ system: OBS_CATEGORY_SYSTEM, code: "exam" }] }],
      code: { coding: [{ system: QUESTIONNAIRE_SCORE_SYSTEM, code: input.scoreId, display: input.label }] },
      ...(input.value === undefined ? {} : scoreValue(input.value, input.unit)),
      ...(input.band ? { interpretation: [{ text: input.band }] } : {}),
      ...(input.dataAbsentReason ? { dataAbsentReason: input.dataAbsentReason } : {}),
      effectiveDateTime: input.at,
      performanceMeasureId: input.measureId,
    });
  }

  get scoreId(): MemberId | undefined {
    return this.state.code.coding?.find((coding) => coding.system === QUESTIONNAIRE_SCORE_SYSTEM)?.code as
      | MemberId
      | undefined;
  }

  /** The band is recorded as the interpretation, so it moves with the value. */
  rescore(value: number, at: string, band?: string, unit?: UnitValue): void {
    if (this.isVoid) return;
    const next = scoreValue(value, unit);
    if (
      this.state.valueInteger !== next.valueInteger ||
      this.state.valueQuantity?.value !== next.valueQuantity?.value ||
      this.state.valueQuantity?.unit !== next.valueQuantity?.unit ||
      this.state.valueQuantity?.code !== next.valueQuantity?.code ||
      this.state.valueQuantity?.system !== next.valueQuantity?.system
    )
      this.correct({ valueInteger: undefined, valueQuantity: undefined, ...next, effectiveDateTime: at });
    if (this.state.interpretation?.[0]?.text !== band)
      this.revise({ interpretation: band ? [{ text: band }] : undefined });
  }

  // ── facts ──

  get id(): ObservationId {
    return this.state.id;
  }

  get status(): ObservationStatus {
    return this.state.status;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get code(): CodeableConcept {
    return structuredClone(this.state.code);
  }

  /** Someone stands behind this value — it may be cited. */
  get isFinal(): boolean {
    return STANDS.has(this.state.status);
  }

  /** Withdrawn or never valid. Nothing may be written to it and nothing should cite it. */
  get isVoid(): boolean {
    return TERMINAL.has(this.state.status);
  }

  snapshot(): ObservationProps {
    return structuredClone(this.state);
  }

  // ── lifecycle ──

  /** Stand behind the value. */
  finalize(): void {
    this.state.status = this.next("final");
  }

  /** Withdraw it — it was taken, but should not count. */
  cancel(): void {
    this.state.status = this.next("cancelled");
  }

  /** It should never have been recorded. */
  retract(): void {
    this.state.status = this.next("entered-in-error");
  }

  /**
   * Mark the record as revisited without saying what changed — an amendment
   * whose substance lives somewhere else (a note, a corrected panel member).
   * Only reachable from a value someone stood behind: amending a draft is
   * meaningless, because a draft is edited outright.
   */
  amend(): void {
    this.state.status = this.next("amended");
  }

  // ── the value ──

  /**
   * Correct what was recorded. On a finalised observation this advances to
   * `amended`, which is FHIR's corrected-record trail; on a draft it is a plain
   * edit. Callers do not choose — the observation's own state decides, so a
   * silent edit to a finalised value is not expressible.
   */
  correct(changes: Partial<Value> & { component?: Component[]; note?: string; effectiveDateTime?: string }): void {
    this.assertWritable("correct");
    Object.assign(this.state, changes);
    if (STANDS.has(this.state.status)) this.state.status = this.next("amended");
  }

  /** Revise the clinical reading around the value — how it is categorised or interpreted. */
  revise(changes: {
    category?: CodeableConcept[];
    interpretation?: CodeableConcept[];
    bodySite?: CodeableConcept;
    note?: string;
  }): void {
    this.assertWritable("revise");
    Object.assign(this.state, changes);
    if (STANDS.has(this.state.status)) this.state.status = this.next("amended");
  }

  // ── internals ──

  private next(to: ObservationStatus): ObservationStatus {
    if (!TRANSITIONS[this.state.status]?.includes(to)) {
      throw new InvalidTransitionError(this.state.status, `Observation:${this.state.status}→${to}`);
    }
    return to;
  }

  private assertWritable(action: string): void {
    if (this.isVoid) {
      throw new PreconditionFailedError(`Cannot ${action} an observation in terminal status '${this.state.status}'`);
    }
  }
}

/** `category` presets for symptom observations — how the finding was come by. */
export const SYMPTOM_CATEGORY: Record<"PATIENT_REPORTED" | "CLINICIAN_OBSERVED", CodeableConcept> = {
  PATIENT_REPORTED: { coding: [{ system: OBS_CATEGORY_SYSTEM, code: "survey", display: "Survey" }] },
  CLINICIAN_OBSERVED: { coding: [{ system: OBS_CATEGORY_SYSTEM, code: "exam", display: "Exam" }] },
};

/** Nonclinical performers currently retain exact external resource coordinates. */
export type ObservationPerformerResourceId = Brand<string, "ObservationPerformerResourceId">;
export function asObservationPerformerResourceId(value: string): ObservationPerformerResourceId {
  return value as ObservationPerformerResourceId;
}
export type ObservationPerformerRef =
  | { type: "Patient"; id: PatientId }
  | { type: "Practitioner"; id: PractitionerId }
  | { type: "resource"; resourceType: string; id: ObservationPerformerResourceId };

/** Keep the UCUM code and human-readable display distinct, as the FHIR unit mapper does. */
function scoreValue(value: number, unit?: UnitValue): Pick<Value, "valueInteger" | "valueQuantity"> {
  return !unit && Number.isInteger(value)
    ? { valueInteger: value }
    : { valueQuantity: { value, ...(unit ? { unit: unit.display, code: unit.code, system: UCUM_SYSTEM } : {}) } };
}
